import React, { useId, useState } from "react";
import "./Disclosure.css";

/**
 * Show/hide for long copy, so a phone visitor isn't faced with a wall of
 * text. A real <button> carries aria-expanded and aria-controls; the panel
 * uses the `hidden` attribute while closed, so its content is out of the
 * tab order and the accessibility tree until opened.
 *
 * Disclosure: a section heading that opens its section.
 * ReadMore: a "Read more" button after the visible text.
 */

/**
 * @param {object} props
 * @param {string} props.title - Heading text (also the button's name)
 * @param {2|3|4} [props.level] - Heading level, default 2
 * @param {boolean} [props.defaultOpen]
 * @param {React.ReactNode} props.children
 */
export function Disclosure({ title, level = 2, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `disclosure-${useId().replace(/:/g, "")}`;
  const Heading = `h${level}`;
  return (
    <div className={`disclosure ${open ? "open" : ""}`}>
      <Heading className="disclosure-heading">
        <button
          type="button"
          className="disclosure-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{title}</span>
          <span className="chev" aria-hidden="true">{open ? "–" : "+"}</span>
        </button>
      </Heading>
      <div id={panelId} className="disclosure-panel" hidden={!open}>
        {children}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} [props.label] - Button text while closed
 * @param {string} [props.lessLabel] - Button text while open
 * @param {string} [props.context] - Screen-reader-only suffix naming what
 *   opens, e.g. the bundle title, when several Read more buttons share a page
 * @param {React.ReactNode} props.children
 */
export function ReadMore({ label = "Read more", lessLabel = "Read less", context, children }) {
  const [open, setOpen] = useState(false);
  const panelId = `read-more-${useId().replace(/:/g, "")}`;
  return (
    <div className="read-more">
      <div id={panelId} className="read-more-panel" hidden={!open}>
        {children}
      </div>
      <button
        type="button"
        className="read-more-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? lessLabel : label}
        {context ? <span className="sr-only">: {context}</span> : null}
      </button>
    </div>
  );
}

export default Disclosure;
